/**
 * CategoriesPage (카테고리 관리 화면)
 *
 * 카테고리 목록 조회 및 CRUD 작업을 수행하는 페이지
 *
 * 책임:
 * - 수입/지출 탭 전환
 * - 대분류/소분류 계층 표시
 * - 카테고리 추가/수정/삭제
 * - 카테고리 정렬 (sortOrder 변경)
 */

import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useCategoryCRUD } from '../hooks/useCategoryCRUD'
import { CategoryFormModal } from '../components/CategoryFormModal'
import { useToast } from '@/shared/components/ui/Toast'
import type { Category, CategoryType } from '../types/Category'

export function CategoriesPage() {
  const navigate = useNavigate()
  const toast = useToast()
  const { data, loading, error, loadByType, create, update, remove } =
    useCategoryCRUD()

  const [activeTab, setActiveTab] = useState<CategoryType>('expense')
  const [showModal, setShowModal] = useState(false)
  const [editingId, setEditingId] = useState<string | null>(null)
  const [parentIdForNew, setParentIdForNew] = useState<string | null>(null)

  /**
   * 탭 변경 시 데이터 로드
   */
  useEffect(() => {
    loadByType(activeTab)
  }, [activeTab])

  /**
   * 대분류 추가 시작
   */
  const handleAddMajor = () => {
    setEditingId(null)
    setParentIdForNew(null)
    setShowModal(true)
  }

  /**
   * 소분류 추가 시작
   */
  const handleAddMinor = (parentId: string) => {
    setEditingId(null)
    setParentIdForNew(parentId)
    setShowModal(true)
  }

  /**
   * 카테고리 수정 시작
   */
  const handleEdit = (category: Category) => {
    setEditingId(category.id)
    setParentIdForNew(null)
    setShowModal(true)
  }

  /**
   * 카테고리 삭제
   */
  const handleDelete = async (id: string, name: string) => {
    if (!confirm(`"${name}" 카테고리를 삭제하시겠습니까?`)) return

    try {
      await remove(id)
      toast.success('삭제되었습니다')
      await loadByType(activeTab)
    } catch (err) {
      console.error('카테고리 삭제 실패:', err)
      toast.error(`삭제 실패: ${err instanceof Error ? err.message : '알 수 없는 오류'}`)
    }
  }

  /**
   * 카테고리 위로 이동
   */
  const handleMoveUp = async (category: Category) => {
    const siblings = category.parentId
      ? data
          .find((major) => major.id === category.parentId)
          ?.children?.filter((c) => c.isActive) || []
      : data.filter((c) => c.isActive)

    const currentIndex = siblings.findIndex((c) => c.id === category.id)
    if (currentIndex <= 0) return // 이미 최상단

    const prevCategory = siblings[currentIndex - 1]
    if (!prevCategory) return // 안전 체크

    try {
      // sortOrder 교환
      await update(category.id, { sortOrder: prevCategory.sortOrder })
      await update(prevCategory.id, { sortOrder: category.sortOrder })
      await loadByType(activeTab)
    } catch (err) {
      console.error('정렬 실패:', err)
      toast.error(`정렬 실패: ${err instanceof Error ? err.message : '알 수 없는 오류'}`)
    }
  }

  /**
   * 카테고리 아래로 이동
   */
  const handleMoveDown = async (category: Category) => {
    const siblings = category.parentId
      ? data
          .find((major) => major.id === category.parentId)
          ?.children?.filter((c) => c.isActive) || []
      : data.filter((c) => c.isActive)

    const currentIndex = siblings.findIndex((c) => c.id === category.id)
    if (currentIndex >= siblings.length - 1) return // 이미 최하단

    const nextCategory = siblings[currentIndex + 1]
    if (!nextCategory) return // 안전 체크

    try {
      // sortOrder 교환
      await update(category.id, { sortOrder: nextCategory.sortOrder })
      await update(nextCategory.id, { sortOrder: category.sortOrder })
      await loadByType(activeTab)
    } catch (err) {
      console.error('정렬 실패:', err)
      toast.error(`정렬 실패: ${err instanceof Error ? err.message : '알 수 없는 오류'}`)
    }
  }

  /**
   * 모달 닫기
   */
  const handleCloseModal = () => {
    setShowModal(false)
    setEditingId(null)
    setParentIdForNew(null)
  }

  /**
   * 카테고리 저장 (생성/수정)
   */
  const handleSave = async (input: Omit<Category, 'id'>) => {
    try {
      if (editingId) {
        await update(editingId, input)
        toast.success('수정되었습니다')
      } else {
        await create(input)
        toast.success('추가되었습니다')
      }
      await loadByType(activeTab)
      handleCloseModal()
    } catch (err) {
      console.error('카테고리 저장 실패:', err)
      toast.error(`저장 실패: ${err instanceof Error ? err.message : '알 수 없는 오류'}`)
    }
  }

  /**
   * 로딩 상태
   */
  if (loading && data.length === 0) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 dark:border-primary-500 mb-4" />
          <p className="text-gray-600 dark:text-gray-400">카테고리를 불러오는 중...</p>
        </div>
      </div>
    )
  }

  /**
   * 에러 상태
   */
  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center p-4">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6 max-w-md w-full">
          <div className="text-center mb-4">
            <span className="text-6xl">⚠️</span>
          </div>
          <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-2 text-center">
            데이터를 불러올 수 없습니다
          </h2>
          <p className="text-red-600 dark:text-red-400 text-sm text-center mb-4">
            {error.message}
          </p>
          <button
            onClick={() => window.location.reload()}
            className="w-full px-4 py-2 bg-primary-600 dark:bg-primary-500 text-white rounded-lg hover:bg-primary-700 dark:hover:bg-primary-600 transition-colors"
          >
            다시 시도
          </button>
        </div>
      </div>
    )
  }

  /**
   * 메인 컨텐츠
   */
  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 pb-20">
      {/* 헤더 */}
      <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-4 py-4">
        <button
          onClick={() => navigate('/settings')}
          className="text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 mb-2 flex items-center gap-1"
        >
          ← 뒤로 가기
        </button>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">카테고리 관리</h1>
      </div>

      {/* 탭 */}
      <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-4 flex">
        <button
          onClick={() => setActiveTab('expense')}
          className={`flex-1 py-3 text-center font-medium transition-colors ${
            activeTab === 'expense'
              ? 'text-primary-600 dark:text-primary-400 border-b-2 border-primary-600 dark:border-primary-500'
              : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
          }`}
        >
          지출
        </button>
        <button
          onClick={() => setActiveTab('income')}
          className={`flex-1 py-3 text-center font-medium transition-colors ${
            activeTab === 'income'
              ? 'text-primary-600 dark:text-primary-400 border-b-2 border-primary-600 dark:border-primary-500'
              : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
          }`}
        >
          수입
        </button>
      </div>

      {/* 카테고리 목록 */}
      <div className="px-4 py-6 space-y-3">
        {data.length === 0 ? (
          <div className="text-center py-12">
            <span className="text-6xl block mb-4">📁</span>
            <p className="text-gray-600 dark:text-gray-400 mb-4">
              등록된 {activeTab === 'expense' ? '지출' : '수입'} 카테고리가
              없습니다
            </p>
            <button
              onClick={handleAddMajor}
              className="px-6 py-3 bg-primary-600 dark:bg-primary-500 text-white rounded-lg hover:bg-primary-700 dark:hover:bg-primary-600 transition-colors"
            >
              대분류 추가하기
            </button>
          </div>
        ) : (
          data.map((major) => (
            <div key={major.id} className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-4">
              {/* 대분류 */}
              <div className="flex items-center gap-3 mb-3">
                <span className="text-2xl">{major.icon}</span>
                <div className="flex-1">
                  <h3 className="font-semibold text-gray-900 dark:text-gray-100">{major.name}</h3>
                </div>
                <div className="flex gap-1">
                  <button
                    onClick={() => handleMoveUp(major)}
                    className="px-2 py-1 text-xs bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-600 dark:text-gray-300 rounded"
                  >
                    ↑
                  </button>
                  <button
                    onClick={() => handleMoveDown(major)}
                    className="px-2 py-1 text-xs bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-600 dark:text-gray-300 rounded"
                  >
                    ↓
                  </button>
                  <button
                    onClick={() => handleEdit(major)}
                    className="px-2 py-1 text-xs bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-600 dark:text-gray-300 rounded"
                  >
                    수정
                  </button>
                  {!major.isDefault && (
                    <button
                      onClick={() => handleDelete(major.id, major.name)}
                      className="px-2 py-1 text-xs bg-red-100 dark:bg-red-900/30 hover:bg-red-200 dark:hover:bg-red-900/50 text-red-600 dark:text-red-300 rounded"
                    >
                      삭제
                    </button>
                  )}
                </div>
              </div>

              {/* 소분류 추가 버튼 */}
              <button
                onClick={() => handleAddMinor(major.id)}
                className="text-sm text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 mb-2"
              >
                + 소분류 추가
              </button>

              {/* 소분류 목록 */}
              {major.children && major.children.length > 0 && (
                <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-3 space-y-2">
                  {major.children.map((minor) => (
                    <div
                      key={minor.id}
                      className="flex items-center gap-2 text-sm"
                    >
                      <span className="text-lg">{minor.icon}</span>
                      <span className="flex-1 text-gray-700 dark:text-gray-200">{minor.name}</span>
                      <div className="flex gap-1">
                        <button
                          onClick={() => handleMoveUp(minor)}
                          className="px-2 py-1 text-xs bg-gray-200 dark:bg-gray-600 hover:bg-gray-300 dark:hover:bg-gray-500 text-gray-600 dark:text-gray-300 rounded"
                        >
                          ↑
                        </button>
                        <button
                          onClick={() => handleMoveDown(minor)}
                          className="px-2 py-1 text-xs bg-gray-200 dark:bg-gray-600 hover:bg-gray-300 dark:hover:bg-gray-500 text-gray-600 dark:text-gray-300 rounded"
                        >
                          ↓
                        </button>
                        <button
                          onClick={() => handleEdit(minor)}
                          className="px-2 py-1 text-xs bg-gray-200 dark:bg-gray-600 hover:bg-gray-300 dark:hover:bg-gray-500 text-gray-600 dark:text-gray-300 rounded"
                        >
                          수정
                        </button>
                        {!minor.isDefault && (
                          <button
                            onClick={() => handleDelete(minor.id, minor.name)}
                            className="px-2 py-1 text-xs bg-red-100 dark:bg-red-900/30 hover:bg-red-200 dark:hover:bg-red-900/50 text-red-600 dark:text-red-300 rounded"
                          >
                            삭제
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))
        )}
      </div>

      {/* 대분류 추가 버튼 (고정) */}
      {data.length > 0 && (
        <div className="fixed bottom-20 right-4">
          <button
            onClick={handleAddMajor}
            className="w-14 h-14 bg-primary-600 dark:bg-primary-500 text-white rounded-full shadow-lg hover:bg-primary-700 dark:hover:bg-primary-600 transition-colors flex items-center justify-center text-2xl"
          >
            +
          </button>
        </div>
      )}

      {/* 추가/수정 모달 */}
      <CategoryFormModal
        show={showModal}
        editingCategory={editingId ? findCategoryById(data, editingId) : null}
        parentId={parentIdForNew}
        type={activeTab}
        majorCategories={data}
        onClose={handleCloseModal}
        onSave={handleSave}
      />
    </div>
  )
}

/**
 * 카테고리 ID로 검색 (대분류/소분류 모두 포함)
 */
function findCategoryById(
  categories: Category[],
  id: string
): Category | null {
  for (const major of categories) {
    if (major.id === id) return major
    if (major.children) {
      const minor = major.children.find((c) => c.id === id)
      if (minor) return minor
    }
  }
  return null
}
